import * as I from 'immutable';
import React from 'react';
import {NavigationType, useLocation, useNavigationType} from 'react-router-dom';
import {BooleanParam, NumberParam, StringParam, useQueryParams} from 'use-query-params';

import {chatToolPermissionsFor} from 'app/components/AgentChat/chatToolPermissions';
import {chatPreset} from 'app/components/AgentChat/gettingStartedPrompts';
import {CurrentView, MapControls} from 'app/components/AgentChat/types';
import type {InitialChat} from 'app/components/AgentChat/useChatSession';
import CenteredSpinner from 'app/components/CenteredSpinner';
import {LayoutWrapper} from 'app/components/LayoutWrapper';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {useSavedPromptsQuery} from 'app/modules/SavedPrompts';
import {LoggedInAuth, LoggedInUserActions} from 'app/providers/AuthProvider';
import * as routeUtils from 'app/utils/routeUtils';

import cs from './styles.module.scss';

// Keeps the Agents SDK and AI SDK out of the main bundle — see ChatPanel.
const ChatSession = React.lazy(() => import('app/components/AgentChat'));

function chatMessageFrom(state: unknown): string | null {
  const message =
    typeof state === 'object' && state !== null
      ? (state as Record<string, unknown>)[routeUtils.CHAT_MESSAGE_STATE_KEY]
      : undefined;
  return typeof message === 'string' ? message.trim() || null : null;
}

interface Props {
  auth: LoggedInAuth;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  organization: I.ImmutableOf<ApiOrganization>;
  loggedInUserActions: LoggedInUserActions;
}

/**
 * Full-page chat, with the agent's canvas beside it. There's no map here, so of
 * the map tools this surface can only fulfil `get_current_view` — and it answers
 * with the org alone, which is enough for the agent to know it has to ask before
 * assuming a property.
 */
export default function AgentChatPage({auth, profile, organization, loggedInUserActions}: Props) {
  const mapControls: MapControls = React.useMemo(() => {
    const view: CurrentView = {
      organization: {id: organization.get('id'), name: organization.get('name')},
      portfolio: null,
      property: null,
      selectedProperties: [],
      sourceId: null,
      layerKey: null,
      date: null,
      compareDate: null,
      compareSourceId: null,
      compareLayerKey: null,
      mode: null,
      camera: null,
      bounds: null,
      openPane: null,
      activeTool: null,
      visibleOverlays: [],
      drawnGeometry: null,
      focusedNoteId: null,
    };
    return {getCurrentView: () => view};
  }, [organization]);

  const toolPermissions = chatToolPermissionsFor(profile, organization);

  // What the link into the page asked for, read once. The params are dropped once the tab has
  // picked its chat, so a reload is a plain visit.
  const [link, setLink] = useQueryParams({
    chat: StringParam,
    new: BooleanParam,
    preset: StringParam,
    workflow: NumberParam,
  });
  const location = useLocation();
  const navigationType = useNavigationType();
  const [arrival] = React.useState(() => {
    const chat: InitialChat | null = link.chat
      ? {kind: 'open', id: link.chat}
      : link.new
        ? {kind: 'new'}
        : null;
    return {
      chat,
      // A reload or Back and Forward is a POP, so only following the link sends its message.
      message: navigationType === NavigationType.Push ? chatMessageFrom(location.state) : null,
      starter: chatPreset(link.preset),
      workflowId: link.workflow ?? null,
    };
  });
  const dropLink = React.useCallback(
    () =>
      setLink(
        {
          chat: undefined,
          new: undefined,
          preset: undefined,
          workflow: undefined,
        },
        'replaceIn'
      ),
    [setLink]
  );

  const {data: savedPrompts} = useSavedPromptsQuery(organization.get('id'), {
    enabled: arrival.workflowId !== null,
  });
  const workflow = savedPrompts?.find((prompt) => prompt.id === arrival.workflowId);

  return (
    <LayoutWrapper
      auth={auth}
      profile={profile}
      organization={organization}
      loggedInUserActions={loggedInUserActions}
    >
      <div className={cs.page}>
        <React.Suspense fallback={<CenteredSpinner />}>
          <ChatSession
            firebaseUid={auth.firebaseUid}
            firebaseToken={auth.firebaseToken}
            mapControls={mapControls}
            toolPermissions={toolPermissions}
            canvas
            starter={arrival.starter}
            initialDraft={workflow?.prompt}
            initialMessage={arrival.message}
            initialChat={arrival.chat}
            onInitialChatReady={dropLink}
          />
        </React.Suspense>
      </div>
    </LayoutWrapper>
  );
}
